import 'css/src/components/common/video-preview/VideoPreview';

import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { ConnectedProps } from 'react-redux';
import type Immutable from 'immutable';
import classNames from 'classnames';
import type Luminary from '@lumen5/luminary';

import type { VideoRender } from 'js/src/client';
import type { LuminaryVideoWithScrubberHandle } from 'js/src/components/common/video-preview/LuminaryVideoWithScrubber';
import LuminaryVideoWithScrubber from 'js/src/components/common/video-preview/LuminaryVideoWithScrubber';
import MuteToggle from 'js/src/components/common/video-preview/MuteToggle';
import type { VideoWithScrubberHandle } from 'js/src/components/common/video-preview/VideoWithScrubber';
import VideoWithScrubber from 'js/src/components/common/video-preview/VideoWithScrubber';

type PreviewElementHandle = LuminaryVideoWithScrubberHandle | VideoWithScrubberHandle;
import type { ImmutableMap } from 'js/src/libs/consts';
import { luminaryReduxConnect } from 'js/src/luminary';

export enum PLAYBACK_STATE {
    LOADING = 'loading',
    PLAYING = 'playing',
    PAUSED = 'paused',
    STOPPED = 'stopped',
}
export type SortedVideoSourceList = Immutable.List<ImmutableMap<VideoRender>>;
type VideoPreviewOwnProps = {
    autoplay?: boolean;
    enableKeyboardShortcuts?: boolean;
    startMuted?: boolean;
    isHtmlVideo?: boolean;
    shouldVideoPause?: boolean;
    onBeforePlay?: () => void;
    onPlay?: () => void;
    onSeek?: (newTime: number) => void;
    numOpenCommentsPerScene?: Immutable.Map<string, number>;
    commentMarkerCallback?: (time: number, forceCommentingMode: boolean) => void;
    enableComments?: boolean;
    showMuteIndicator?: boolean;
    sortedVideoSourceList?: SortedVideoSourceList;
    onCurrentTimeUpdate?: (currentTime: number) => void;
};
type VideoPreviewPropsFromRedux = ConnectedProps<typeof VideoPreviewConnector>;

type Props = VideoPreviewOwnProps & VideoPreviewPropsFromRedux;

const SEEK_DELTA_MS = 10000;
const FRAME_MS = 1000 / 30;

const VideoPreviewPresentation = React.memo(function VideoPreview({
    autoplay,
    enableKeyboardShortcuts = false,
    aspectRatio,
    startMuted,
    isHtmlVideo,
    shouldVideoPause,
    onBeforePlay,
    onPlay,
    onSeek,
    numOpenCommentsPerScene,
    commentMarkerCallback,
    enableComments,
    showMuteIndicator,
    sortedVideoSourceList,
    onCurrentTimeUpdate,
}: Props) {
    const [playback, setPlayback] = useState(PLAYBACK_STATE.LOADING);
    const [muted, setMuted] = useState(startMuted === true);
    const previewElementRef = useRef<PreviewElementHandle>(null);

    const callbackRef = useRef<(() => void) | null>(null);

    const setPlaybackState = useCallback(
        (newPlaybackState: PLAYBACK_STATE, callback?: () => void) => {
            callbackRef.current = callback ?? null;
            setPlayback(newPlaybackState);
        },
        [],
    );

    useEffect(() => {
        if (callbackRef.current) {
            callbackRef.current();
            callbackRef.current = null;
        }
    }, [playback]);

    const setMutedState = useCallback((muteLevel: boolean) => {
        setMuted(muteLevel);
    }, []);

    const toggleMute = useCallback(() => {
        setMuted((m) => !m);
    }, []);

    const play = useCallback(
        (opts = { firstPlay: false }) => {
            typeof onBeforePlay === 'function' && onBeforePlay();
            if (!previewElementRef.current) throw new Error('No preview element ref found');
            previewElementRef.current.play(opts);
            typeof onPlay === 'function' && onPlay();
        },
        [onBeforePlay, onPlay],
    );

    const pause = useCallback(() => {
        if (!previewElementRef.current) throw new Error('No preview element ref found');
        previewElementRef.current.pause();
    }, []);

    const togglePlay = useCallback(() => {
        if (playback === PLAYBACK_STATE.PLAYING) {
            pause();
        } else {
            play();
        }
    }, [playback, pause, play]);

    useEffect(() => {
        if (!enableKeyboardShortcuts) return;
        const onKeyDown = (e: KeyboardEvent) => {
            const target = e.target;
            if (
                target instanceof HTMLInputElement ||
                target instanceof HTMLTextAreaElement ||
                target instanceof HTMLSelectElement ||
                target instanceof HTMLButtonElement ||
                (target instanceof HTMLElement && target.isContentEditable) ||
                (target instanceof HTMLElement && target.closest('[contenteditable="true"]'))
            ) {
                return;
            }

            const el = previewElementRef.current;
            if (
                !el ||
                typeof el.getCurrentTime !== 'function' ||
                typeof el.getDuration !== 'function' ||
                typeof el.seek !== 'function'
            ) {
                return;
            }
            const currentTime = el.getCurrentTime();
            const duration = el.getDuration();
            const shift = e.shiftKey;
            const key = e.key.toLowerCase();
            switch (key) {
                case 'j':
                case 'arrowleft':
                    if (shift) {
                        el.seek(Math.max(0, currentTime - FRAME_MS));
                    } else {
                        el.seek(Math.max(0, currentTime - SEEK_DELTA_MS));
                    }
                    e.preventDefault();
                    break;
                case 'k':
                case ' ':
                    togglePlay();
                    e.preventDefault();
                    break;
                case 'm':
                    toggleMute();
                    e.preventDefault();
                    break;
                case 'l':
                case 'arrowright':
                    if (shift) {
                        el.seek(Math.min(duration, currentTime + FRAME_MS));
                    } else {
                        el.seek(Math.min(duration, currentTime + SEEK_DELTA_MS));
                    }
                    e.preventDefault();
                    break;
                default:
                    break;
            }
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [enableKeyboardShortcuts, togglePlay, toggleMute]);

    useEffect(() => {
        if (autoplay !== false) {
            play({ firstPlay: true });
        }
    }, [autoplay, play]);

    useEffect(() => {
        if (shouldVideoPause) {
            pause();
        }
    }, [shouldVideoPause, pause]);

    const videoProps = {
        autoplay: autoplay,
        onSeek: onSeek,
        numOpenCommentsPerScene: numOpenCommentsPerScene,
        commentMarkerCallback: commentMarkerCallback,
        pauseVideo: pause,
        enableComments: enableComments,
        playbackState: playback,
        setPlaybackState: setPlaybackState,
        muted: muted,
        onCurrentTimeUpdate: onCurrentTimeUpdate,
    };
    const playPauseReplayOverlay = (
        <div className="video-preview-overlay d-flex justify-content-center align-items-center position-absolute top-0 start-0 end-0 w-100">
            <div
                className={classNames('text-center video-preview-play-pause p-l5-8', {
                    'show-on-hover': playback === PLAYBACK_STATE.PLAYING,
                })}
            >
                <span className="material-symbols-outlined material-symbols-f1">
                    {playback === PLAYBACK_STATE.STOPPED
                        ? 'replay'
                        : playback === PLAYBACK_STATE.PLAYING
                          ? 'pause'
                          : 'play_arrow'}
                </span>
            </div>
        </div>
    );

    let videoElement: React.ReactNode;
    if (isHtmlVideo) {
        if (sortedVideoSourceList === undefined) {
            throw new Error('sortedVideoSourceList is required for HTML video');
        }
        videoElement = (
            <VideoWithScrubber
                ref={previewElementRef as React.RefObject<VideoWithScrubberHandle>}
                sortedVideoSourceList={sortedVideoSourceList}
                {...videoProps}
            />
        );
    } else {
        videoElement = (
            <LuminaryVideoWithScrubber
                ref={previewElementRef as React.RefObject<LuminaryVideoWithScrubberHandle>}
                {...videoProps}
            />
        );
    }

    return (
        <div className={`video-preview ${aspectRatio}`}>
            <div
                className={classNames('top-corner-radius cursor-pointer', {
                    stopped: playback === PLAYBACK_STATE.STOPPED,
                })}
                onClick={togglePlay}
            >
                {playPauseReplayOverlay}
                {/* mute button */}
                {showMuteIndicator && <MuteToggle muted={muted} toggleMute={setMutedState} />}

                {/* main slide markers + luminary canvas/video element */}
                {videoElement}
            </div>
        </div>
    );
});

const mapLuminaryStateToProps = (luminary: Luminary) => {
    return {
        aspectRatio: luminary.aspectRatio,
    };
};

/**
    The VideoPreview component uses luminaryReduxConnect to interact with whatever instance
    of luminary is already located in the component hierarchy. If you don't want to use
    an existing instance of luminary on the page, just wrap it in it's own LuminaryContextProvider:

    <LuminaryContextProvider minDimensionsResolution={###}>
        <VideoPreview />
    </LuminaryContextProvider>
*/
const VideoPreviewConnector = luminaryReduxConnect(undefined, undefined, mapLuminaryStateToProps);
const VideoPreview = VideoPreviewConnector(VideoPreviewPresentation);

export default VideoPreview;
